/**
 * État de session, partagé par toute l'application.
 *
 * Trois états et non deux : « connecté », « visiteur », et **« on ne sait pas encore »**.
 * Le troisième est celui qu'on oublie, et son oubli se voit : au premier rendu, avant
 * que le serveur ait répondu, l'application afficherait l'écran de connexion à quelqu'un
 * de parfaitement connecté. Un clignotement d'un demi-seconde à chaque rechargement, qui
 * donne l'impression d'un produit fragile.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { type Identity, logout as closeSession, me } from "../authApi";
import { onSessionLost } from "../http";

type Status = "unknown" | "authenticated" | "anonymous";

interface SessionValue {
  status: Status;
  identity: Identity | null;
  /** Appelé après une connexion ou une inscription réussie. */
  adopt: (identity: Identity) => void;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("unknown");
  const [identity, setIdentity] = useState<Identity | null>(null);

  // Une seule interrogation au démarrage. Le cookie est `HttpOnly` : c'est la seule
  // façon de savoir si la session tient encore.
  useEffect(() => {
    let vivant = true;
    me()
      .then((found) => {
        if (!vivant) return;
        setIdentity(found);
        setStatus(found ? "authenticated" : "anonymous");
      })
      .catch(() => {
        // Une API injoignable n'est pas une session absente, mais l'utilisateur ne peut
        // rien faire d'autre que se reconnecter — et l'écran de connexion lui dira
        // clairement pourquoi sa tentative échoue.
        if (vivant) setStatus("anonymous");
      });
    return () => {
      vivant = false;
    };
  }, []);

  // Toute requête revenant en 401 ramène ici, où qu'elle ait été lancée. C'est ce qui
  // fait qu'une session expirée en cours d'analyse ne produit pas un message énigmatique
  // mais un retour propre à la connexion.
  useEffect(
    () =>
      onSessionLost(() => {
        setIdentity(null);
        setStatus("anonymous");
      }),
    [],
  );

  const adopt = useCallback((found: Identity) => {
    setIdentity(found);
    setStatus("authenticated");
  }, []);

  const signOut = useCallback(async () => {
    // L'interface bascule d'abord : attendre le réseau pour retirer l'écran donnerait
    // l'impression que le bouton n'a pas fonctionné.
    setIdentity(null);
    setStatus("anonymous");
    await closeSession();
  }, []);

  const value = useMemo<SessionValue>(
    () => ({ status, identity, adopt, signOut }),
    [status, identity, adopt, signOut],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) {
    throw new Error("useSession doit être utilisé dans un <SessionProvider>.");
  }
  return value;
}
